import { useState, useCallback } from "react";
import firebaseService from "firebase_service/firebaseService";
import { Ticket } from "../types";
import { calculateInsertionOrder } from "../utils";

type OrderMap = Record<string, number>;

function getStorageKey(): string | null {
  const userId = firebaseService.getUserId();
  if (!userId) return null;
  return `ticket-order-${userId}`;
}

function readFromStorage(): OrderMap {
  try {
    const key = getStorageKey();
    if (!key) return {};
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeToStorage(orderMap: OrderMap): void {
  try {
    const key = getStorageKey();
    if (!key) return;
    localStorage.setItem(key, JSON.stringify(orderMap));
  } catch {
    // localStorage quota exceeded — in-memory state still works for the session
  }
}

export function useLocalTicketOrder() {
  const [orderMap, setOrderMap] = useState<OrderMap>(readFromStorage);

  const getEffectiveOrder = useCallback(
    (ticket: Ticket): number => orderMap[ticket.id] ?? ticket.order ?? 0,
    [orderMap],
  );

  const setTicketOrder = useCallback((ticketId: string, newOrder: number) => {
    setOrderMap((previous) => {
      const next = { ...previous, [ticketId]: newOrder };
      writeToStorage(next);
      return next;
    });
  }, []);

  const sortByLocalOrder = useCallback(
    (tickets: Ticket[]): Ticket[] =>
      [...tickets].sort(
        (first, second) => getEffectiveOrder(first) - getEffectiveOrder(second),
      ),
    [getEffectiveOrder],
  );

  const calculateAndSetOrder = useCallback(
    (
      ticketId: string,
      prevTicket: Ticket | null,
      nextTicket: Ticket | null,
    ): number => {
      const prevOrder = prevTicket
        ? { order: getEffectiveOrder(prevTicket) }
        : null;
      const nextOrder = nextTicket
        ? { order: getEffectiveOrder(nextTicket) }
        : null;
      const newOrder = calculateInsertionOrder(prevOrder, nextOrder);
      setTicketOrder(ticketId, newOrder);
      return newOrder;
    },
    [getEffectiveOrder, setTicketOrder],
  );

  return {
    getEffectiveOrder,
    setTicketOrder,
    sortByLocalOrder,
    calculateAndSetOrder,
  };
}
